
/**
 * LocalStorage Chunking Utility
 * Splits large strings into multiple chunks to bypass the ~5MB limit.
 */

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
const CHUNK_PREFIX = '__auteur_chunk_';

/**
 * Saves a large object to localStorage by chunking it.
 * @param key The base key to use
 * @param data The object to save
 */
export const saveToLocalStorageChunked = (key: string, data: any): boolean => {
    try {
        const stringData = JSON.stringify(data);
        const numChunks = Math.ceil(stringData.length / CHUNK_SIZE);

        // 1. Get current number of chunks to clean up if we now have fewer
        const metadataKey = `${key}_metadata`;
        const oldMetadataRaw = localStorage.getItem(metadataKey);
        let oldNumChunks = 0;
        if (oldMetadataRaw) {
            try {
                const oldMetadata = JSON.parse(oldMetadataRaw);
                oldNumChunks = oldMetadata.chunks || 0;
            } catch (e) {
                // Ignore
            }
        }

        // 2. Save chunks
        for (let i = 0; i < numChunks; i++) {
            const chunk = stringData.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            localStorage.setItem(`${CHUNK_PREFIX}${key}_${i}`, chunk);
        }

        // 3. Clean up extra chunks if size decreased
        for (let i = numChunks; i < oldNumChunks; i++) {
            localStorage.removeItem(`${CHUNK_PREFIX}${key}_${i}`);
        }

        // 4. Save metadata
        localStorage.setItem(metadataKey, JSON.stringify({
            chunks: numChunks,
            timestamp: Date.now(),
            version: 'v1'
        }));

        return true;
    } catch (e) {
        console.error("[Storage] Failed to save chunked data:", e);
        return false;
    }
};

/**
 * Loads a chunked object from localStorage.
 * @param key The base key
 * @returns The parsed object or null if not found
 */
export const loadFromLocalStorageChunked = (key: string): any | null => {
    try {
        const metadataKey = `${key}_metadata`;
        const metadataRaw = localStorage.getItem(metadataKey);
        if (!metadataRaw) return null;

        const metadata = JSON.parse(metadataRaw);
        const numChunks = metadata.chunks;

        let combined = '';
        for (let i = 0; i < numChunks; i++) {
            const chunk = localStorage.getItem(`${CHUNK_PREFIX}${key}_${i}`);
            if (chunk === null) {
                console.error(`[Storage] Missing chunk ${i} for key ${key}`);
                return null;
            }
            combined += chunk;
        }

        return JSON.parse(combined);
    } catch (e) {
        console.error("[Storage] Failed to load chunked data:", e);
        return null;
    }
};

/**
 * Removes chunked data from localStorage.
 */
export const removeFromLocalStorageChunked = (key: string) => {
    const metadataKey = `${key}_metadata`;
    const metadataRaw = localStorage.getItem(metadataKey);
    if (!metadataRaw) return;

    try {
        const metadata = JSON.parse(metadataRaw);
        const numChunks = metadata.chunks;
        for (let i = 0; i < numChunks; i++) {
            localStorage.removeItem(`${CHUNK_PREFIX}${key}_${i}`);
        }
        localStorage.removeItem(metadataKey);
    } catch (e) {
        // Ignore
    }
};
